import { Deserializable } from '../../protocols/deserializable';

export class BulkDeleteAssetsRequestAsset implements Deserializable {
  constructor(id?: string, md5Hash?: string) {
    this.id = id ?? '';
    this.md5Hash = md5Hash ?? '';
  }

  public id: string = '';
  public md5Hash: string = '';

  onDeserialize(): void {}
}

/**
 * Payload for WebsocketActionRequestType_BulkDeleteAssets. Every field maps 1:1 onto a parameter of the per-item REST
 * calls this replaces, so the job and the REST fallback delete the same things:
 *
 *   assets[].id       -> delete-asset?Id=
 *   assets[].md5Hash  -> delete-asset?MD5Hash=
 *   assetFolderIds[]  -> one entry per delete-asset-folder?Id= call
 *   targetFolderId    -> delete-asset-folder?TargetFolderId=   ('ROOT' is the library root)
 *   deleteAllItems    -> delete-asset-folder?DeleteAllItems=
 */
export class BulkDeleteAssetsRequest implements Deserializable {
  constructor(
    assets?: BulkDeleteAssetsRequestAsset[],
    assetFolderIds?: string[],
    targetFolderId?: string,
    deleteAllItems?: boolean
  ) {
    this.assets = assets ?? [];
    this.assetFolderIds = assetFolderIds ?? [];
    this.targetFolderId = targetFolderId ?? '';
    this.deleteAllItems = deleteAllItems ?? false;
  }

  public assets: BulkDeleteAssetsRequestAsset[] = [];
  public assetFolderIds: string[] = [];
  public targetFolderId: string = '';
  public deleteAllItems: boolean = false;

  onDeserialize(): void {
    const Deserialize = window?.injector?.Deserialize;
    this.assets = Deserialize?.arrayOf(BulkDeleteAssetsRequestAsset, this.assets) ?? [];
    this.assetFolderIds = Array.from(this.assetFolderIds || []);
  }

  /**
   * The rules the API validates against, checked here so a user never triggers a negative acknowledgment. Both mode
   * fields are ignored without folders - media always deletes outright.
   */
  validationError(): string | null {
    const hasFolders = this.assetFolderIds?.length > 0;
    if (!this.assets?.length && !hasFolders) {
      return 'At least one media item or folder is required for a bulk delete.';
    }
    if (this.assets?.some(asset => !asset?.id?.trim() || !asset?.md5Hash?.trim())) {
      return 'Every media item in a bulk delete requires an id and an md5 hash.';
    }
    if (this.assetFolderIds?.some(assetFolderId => !assetFolderId?.trim())) {
      return 'Every folder in a bulk delete requires an id.';
    }
    if (hasFolders) {
      if (this.targetFolderId?.trim() && this.deleteAllItems) {
        return 'Folder contents can either be moved to a target folder or deleted, but not both.';
      }
      if (!this.targetFolderId?.trim() && !this.deleteAllItems) {
        return 'Folder contents must either be moved to a target folder or deleted.';
      }
    }
    return null;
  }
}
